import type { ExamScaledReport } from '@examing/types';

export interface ShareCardData {
  exam: 'IELTS' | 'SAT';
  /** The big number: the IELTS overall band ("7.0") or the SAT total ("1340"). */
  headline: string;
  /** Section scores under it, e.g. Listening 7.5. */
  parts: Array<{ label: string; value: string }>;
  testTitle: string;
}

const band = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : null);

/** What the card shows, or null while there is no final score to share (a band still being checked). */
export function shareCardData(report: ExamScaledReport, testTitle: string): ShareCardData | null {
  if (report.standard === 'SAT') {
    if (!Number.isFinite(report.total)) return null;
    const parts = [
      report.rw && { label: 'Reading & Writing', value: String(report.rw.scaled) },
      report.math && { label: 'Math', value: String(report.math.scaled) },
    ].filter(Boolean) as ShareCardData['parts'];
    return { exam: 'SAT', headline: String(report.total), parts, testTitle };
  }
  const overall = band(report.overall);
  if (!overall) return null;
  const parts = [
    ['Listening', band(report.listening?.band)],
    ['Reading', band(report.reading?.band)],
    ['Writing', band(report.writing?.band)],
    ['Speaking', band(report.speaking?.band)],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => ({ label: label as string, value: value as string }));
  return { exam: 'IELTS', headline: overall, parts, testTitle };
}

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;
const INK = '#0B1220';
const LIME = '#A6F24A';

/** Draws the 1080×1350 (Instagram portrait) result card. No network: fonts fall back to the system sans. */
export function drawShareCard(canvas: HTMLCanvasElement, data: ShareCardData, labels: { score: string; cta: string }) {
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const sans = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  // A soft lime glow behind the score.
  const glow = ctx.createRadialGradient(540, 560, 40, 540, 560, 520);
  glow.addColorStop(0, 'rgba(166, 242, 74, 0.28)');
  glow.addColorStop(1, 'rgba(166, 242, 74, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  ctx.textAlign = 'center';
  ctx.fillStyle = LIME;
  ctx.font = `700 44px ${sans}`;
  ctx.fillText(`${data.exam} · ${labels.score}`, 540, 230);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = `800 ${data.exam === 'IELTS' ? 300 : 240}px ${sans}`;
  ctx.fillText(data.headline, 540, 640);

  ctx.font = `500 38px ${sans}`;
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  const title = data.testTitle.length > 42 ? `${data.testTitle.slice(0, 41)}…` : data.testTitle;
  ctx.fillText(title, 540, 740);

  // Section scores in a row of pills.
  const count = data.parts.length;
  if (count) {
    const pillWidth = Math.min(220, (CARD_WIDTH - 160 - (count - 1) * 24) / count);
    const total = count * pillWidth + (count - 1) * 24;
    data.parts.forEach((part, index) => {
      const x = (CARD_WIDTH - total) / 2 + index * (pillWidth + 24);
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.beginPath();
      ctx.roundRect?.(x, 840, pillWidth, 170, 28);
      if (!ctx.roundRect) ctx.rect(x, 840, pillWidth, 170);
      ctx.fill();
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `700 64px ${sans}`;
      ctx.fillText(part.value, x + pillWidth / 2, 935);
      ctx.fillStyle = 'rgba(255,255,255,0.65)';
      ctx.font = `500 ${pillWidth < 200 ? 24 : 28}px ${sans}`;
      ctx.fillText(part.label, x + pillWidth / 2, 980);
    });
  }

  ctx.fillStyle = LIME;
  ctx.font = `800 56px ${sans}`;
  ctx.fillText('examing.uz', 540, 1200);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = `500 32px ${sans}`;
  ctx.fillText(labels.cta, 540, 1254);
}
